import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Heart, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar, 
  Shield, 
  ShieldCheck, 
  UserCheck, 
  UserX,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import Layout from '../../components/Layout';
import { angelService } from '../../services/admin';
import { Angel } from '../../types';

interface AngelDetailViewProps {
  angel: Angel;
  onBack: () => void;
  onVerify: (verified: boolean) => void;
  onStatusChange: (isActive: boolean) => void;
}

const AngelDetailView: React.FC<AngelDetailViewProps> = ({ 
  angel: initialAngel, 
  onBack,
  onVerify,
  onStatusChange
}) => {
  const [angel, setAngel] = useState<Angel>(initialAngel);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'strayyos'>('overview');

  const fetchAngelDetails = async () => {
    try {
      setLoading(true);
      const detailedAngel = await angelService.getById(angel.id);
      setAngel(detailedAngel);
    } catch (err) {
      console.error('Error fetching angel details:', err);
      setError('Failed to fetch angel details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAngelDetails();
  }, [angel.id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusInfo = () => {
    const isVerified = angel.user.isVerified;
    const isActive = angel.isActive;

    if (isVerified && isActive) {
      return {
        color: 'green',
        icon: ShieldCheck,
        text: 'Verified & Active',
        description: 'This angel is verified and actively helping strayyos.'
      };
    } else if (isVerified && !isActive) {
      return {
        color: 'yellow',
        icon: Shield,
        text: 'Verified & Inactive',
        description: 'This angel is verified but currently inactive.'
      };
    } else if (!isVerified && isActive) {
      return {
        color: 'blue',
        icon: UserCheck,
        text: 'Active (Unverified)',
        description: 'This angel is active but not yet verified.'
      };
    } else {
      return {
        color: 'gray',
        icon: UserX,
        text: 'Inactive & Unverified',
        description: 'This angel is neither verified nor active.'
      };
    }
  };

  const handleVerify = async (verified: boolean) => {
    try {
      await onVerify(verified);
      setAngel(prev => ({
        ...prev,
        user: { ...prev.user, isVerified: verified }
      }));
    } catch (err) {
      setError('Failed to update verification status');
    }
  };

  const handleStatusChange = async (isActive: boolean) => {
    try {
      await onStatusChange(isActive);
      setAngel(prev => ({ ...prev, isActive }));
    } catch (err) {
      setError('Failed to update angel status');
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Angels
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {angel.user.name || 'Unnamed Angel'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Angel ID: {angel.id}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {!angel.user.isVerified ? (
              <button
                onClick={() => handleVerify(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Verify Angel
              </button>
            ) : (
              <button
                onClick={() => handleVerify(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Shield className="w-4 h-4 mr-2" />
                Remove Verification
              </button>
            )}

            {angel.isActive ? (
              <button
                onClick={() => handleStatusChange(false)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <UserX className="w-4 h-4 mr-2" />
                Deactivate
              </button>
            ) : (
              <button
                onClick={() => handleStatusChange(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Activate
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error}</div>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-red-600 hover:text-red-500 text-sm underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Angel Info Card */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Heart className="h-8 w-8 text-indigo-600" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {angel.user.name || 'Unnamed Angel'}
                  </h2>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                    statusInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    statusInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    <StatusIcon className="w-4 h-4 mr-1" />
                    {statusInfo.text}
                  </span>
                </div>
                <p className="text-gray-600 mt-1">{statusInfo.description}</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {angel.user.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email</p>
                    <p className="text-sm text-gray-600">{angel.user.email}</p>
                  </div>
                </div>
              )}

              {angel.user.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">{angel.user.phone}</p>
                  </div>
                </div>
              )}

              {angel.location && (
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Location</p>
                    <p className="text-sm text-gray-600">{angel.location}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Joined</p>
                  <p className="text-sm text-gray-600">{formatShortDate(angel.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('strayyos')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'strayyos'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Assigned Strayyos ({angel._count?.strayyos || angel.strayyos?.length || 0})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Users className="h-8 w-8 text-indigo-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Assigned Strayyos</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {angel._count?.strayyos || angel.strayyos?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Calendar className="h-8 w-8 text-green-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Active Since</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {Math.ceil((Date.now() - new Date(angel.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Shield className="h-8 w-8 text-blue-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Status</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {angel.user.isVerified ? 'Verified' : 'Unverified'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Account Details</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">User ID</dt>
                        <dd className="text-sm text-gray-900 font-mono">{angel.user.id}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Angel ID</dt>
                        <dd className="text-sm text-gray-900 font-mono">{angel.id}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Account Created</dt>
                        <dd className="text-sm text-gray-900">{formatDate(angel.user.createdAt || angel.createdAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                        <dd className="text-sm text-gray-900">{formatDate(angel.updatedAt)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'strayyos' && (
              <div>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-2 text-gray-600">Loading strayyos...</span>
                  </div>
                ) : angel.strayyos && angel.strayyos.length > 0 ? (
                  <div className="space-y-4">
                    {angel.strayyos.map((assignment) => (
                      <div key={assignment.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              {assignment.strayyo?.primaryImageUrl ? (
                                <img
                                  src={assignment.strayyo.primaryImageUrl}
                                  alt={assignment.strayyo.name || 'Strayyo'}
                                  className="h-12 w-12 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                  <Heart className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">
                                {assignment.strayyo?.name || assignment.strayyo?.uid || 'Unknown Strayyo'}
                              </h4>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  assignment.role === 'PRIMARY' 
                                    ? 'bg-blue-100 text-blue-800'
                                    : assignment.role === 'ANGEL'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {assignment.role}
                                </span>
                                {assignment.strayyo?.area && (
                                  <span className="flex items-center">
                                    <MapPin className="h-3 w-3 mr-1" />
                                    {assignment.strayyo.area}
                                  </span>
                                )}
                                <span className="flex items-center">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {formatShortDate(assignment.createdAt)}
                                </span>
                              </div>
                              {assignment.notes && (
                                <p className="text-sm text-gray-600 mt-1">{assignment.notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {assignment.strayyo && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                assignment.strayyo.healthStatus === 'GOOD'
                                  ? 'bg-green-100 text-green-800'
                                  : assignment.strayyo.healthStatus === 'FAIR'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : assignment.strayyo.healthStatus === 'POOR'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {assignment.strayyo.healthStatus}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No strayyos assigned</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      This angel hasn't been assigned to any strayyos yet.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AngelDetailView;
